import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { VStack, Text, Center, useDisclosure, Box } from "@chakra-ui/react";
import {
  columnTypes,
  distinctValue,
  GridContainer,
  isMobile,
  Show,
  Spinner,
} from "../../components";
import { tradesColumnDefinitions } from "./tradesColumnDefinitions";
import { getSimpleTradesColumnDefinitions } from "./simpleTradesColumnDefinitions";
import { TradeEx } from "../../types";
import { TradesInnerRenderer } from "./TradesInnerRenderer";
import {
  overlayLoadingTemplate,
  overlayNoRowsTemplate,
} from "../../components/grid/NoRowsToShow";
import {
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
  RowDragEvent,
} from "ag-grid-community";
import { useGridState } from "../../components/grid/useGridState";
import { useGridContextMenuController } from "../../components/grid/utils/useGridContextMenuController";
import EditTradeModal from "../../components/grid/utils/contextMenu/EditTradeModal";
import SearchGroupsModal from "../../components/grid/utils/contextMenu/SearchGroupsModal";
import { useAtomValue } from "jotai";
import { ViewModeAtom, TradesGroupingModeAtom } from "../../store";

interface TradesProps {
  data: TradeEx[];
  loading: boolean;
  selectedTrades: TradeEx[];
  onSelectedRowChanged: (rows: TradeEx[]) => void;
  onRowDragMove: (params: RowDragEvent) => void;
  onRowDragEnd: (params: any, openBookSearchModal?: any, openPositionGroupSearchModal?: any) => void;
  books: string[];
  groupNames: string[];
}

export const Trades = (props: TradesProps) => {
  const { data, selectedTrades, onRowDragMove, onRowDragEnd } = props;

  const gridRef = useRef<GridApi | null>(null);

  const viewMode = useAtomValue(ViewModeAtom);
  const isSimple = viewMode === "simple";
  const groupingMode = useAtomValue(TradesGroupingModeAtom);

  const gridName = isSimple 
    ? `Trades-Simple-${groupingMode}` 
    : "Trades";
  const gridState = useGridState({ gridName });

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridRef.current = e.api;
    gridState.onGridReady(e);
  }, [gridState]);

  const onSelectionChanged = (e: SelectionChangedEvent) => {
    const selectedRows = e.api.getSelectedRows();

    props.onSelectedRowChanged(selectedRows);
  };

  const loadingOverlayComponent = useMemo(() => {
    return overlayLoadingTemplate;
  }, []);

  const loadingOverlayComponentParams = useMemo(() => {
    return {
      loadingMessage: "Retrieving transactions...",
    };
  }, []);

  const noRowsOverlayComponent = useMemo(() => {
    return overlayNoRowsTemplate;
  }, []);

  const noRowsOverlayComponentParams = useMemo(() => {
    return {
      noRowsMessageFunc: () =>
        "No trades found, please add them using the 'Add assets' button ",
    };
  }, []);

  const {
    isOpen: isEditModalOpen,
    onOpen: onOpenEditModal,
    onClose: onCloseEditModal,
  } = useDisclosure();

  const {
    isOpen: isBookSearchModalOpen,
    onOpen: onOpenBookSearchModal,
    onClose: onCloseBookSearchModal,
  } = useDisclosure();

  const {
    isOpen: isPositionGroupSearchModalOpen,
    onOpen: onOpenPositionGroupSearchModal,
    onClose: onClosePositionGroupSearchModal,
  } = useDisclosure();

  const {
    getContextMenuItems,
    selectedParams,
    handleBookSelect,
    handlePositionGroupSelect,
    openBookSearchModal: openBookSearchModalFn,
    openPositionGroupSearchModal: openPositionGroupSearchModalFn,
    freshBooks,
    freshGroupNames,
  } = useGridContextMenuController(
    () => {},
    () => {},
    () => {},
    () => {},
    onOpenEditModal,
    onCloseEditModal,
    onOpenBookSearchModal,
    onCloseBookSearchModal,
    onOpenPositionGroupSearchModal,
    onClosePositionGroupSearchModal
  );

  // Create wrapper functions to handle the drag and drop search modal opening
  const handleOpenBookSearchModal = (params: any) => {
    openBookSearchModalFn(params);
  };

  const handleOpenPositionGroupSearchModal = (params: any) => {
    openPositionGroupSearchModalFn(params);
  };

  // Override the onRowDragEnd function to include our search modal functions
  const onRowDragEndWithModals = (params: any) => {
    onRowDragEnd(
      params,
      handleOpenBookSearchModal,
      handleOpenPositionGroupSearchModal
    );
  };

  useEffect(() => {
    if (selectedTrades.length === 0 && gridRef.current) {
      const nodes = gridRef.current.getSelectedNodes();
      if (nodes) {
        for (const node of nodes) {
          node.setSelected(false);
        }
      }
    }
  }, [selectedTrades]);

  // Reset columns when grouping mode changes in simple mode
  useEffect(() => {
    if (isSimple && gridRef.current) {
      try {
        gridRef.current.setColumnDefs(getSimpleTradesColumnDefinitions(groupingMode));
        gridRef.current.expandAll();
      } catch (error) {
        console.error("Error resetting columns:", error);
      }
    }
  }, [groupingMode, isSimple]);

  const handleGridReady = (e: GridReadyEvent) => {
    onGridReady(e);
    if (isSimple) {
      try {
        e.api.expandAll();
      } catch {}
    }
  };

  const handleFirstDataRendered = () => {
    gridState.onFirstDataRendered();
    if (isSimple && gridRef.current) {
      try {
        gridRef.current.expandAll();
      } catch {}
    }
  };

  return (
    <>
      <Show if={props.loading}>
        <VStack
          pos="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="220px"
          borderRadius={"md"}
          justifyContent={"center"}
          alignItems={"center"}
          bg="var(--bg)"
          border={"1px solid var(--accent)"}
          flexDir={"row"}
          p="4"
          zIndex={100}
        >
          <Spinner size="lg" />
          <Text>Loading Trades...</Text>
        </VStack>
      </Show>
      {/* <Show if={!props.loading}> */}
      <EditTradeModal
        isOpen={isEditModalOpen}
        onClose={onCloseEditModal}
        onConfirm={onCloseEditModal}
        selectedParams={selectedParams}
      />

      <SearchGroupsModal
        isOpen={isBookSearchModalOpen}
        onClose={onCloseBookSearchModal}
        title="Search Books"
        items={freshBooks.length > 0 ? freshBooks : (props.books || [])}
        onSelect={handleBookSelect}
      />
      
      <SearchGroupsModal
        isOpen={isPositionGroupSearchModalOpen}
        onClose={onClosePositionGroupSearchModal}
        title="Search Position Groups"
        items={freshGroupNames.length > 0 ? freshGroupNames : (props.groupNames || [])}
        onSelect={handlePositionGroupSelect}
      />

      <GridContainer>
        <AgGridReact
          key={isSimple ? `Trades-Simple-${groupingMode}` : "Trades-Advanced"}
          //   context={{ instruments: controller.instruments }}
          columnDefs={isSimple ? getSimpleTradesColumnDefinitions(groupingMode) : tradesColumnDefinitions} // Column Defs for Columns
          groupSelectsChildren={true}
          groupSelectsFiltered={true}
          rowData={data} // Row Data for Rows
          columnTypes={columnTypes} // Optional - custom column types
          rowGroupPanelShow={isSimple ? "never" : "always"}
          rowSelection="multiple" // Options - allows click selection of rows
          rowHeight={31}
          suppressAggFilteredOnly={false}
          suppressAggFuncInHeader={true}
          suppressChangeDetection={false}
          suppressMovableColumns={isSimple}
          rowDragManaged={false}
          rowDragEntireRow={isMobile}
          animateRows={true}
          rowDragMultiRow={true}
          getContextMenuItems={getContextMenuItems}
          aggFuncs={{
            distinct: distinctValue,
          }}
          autoGroupColumnDef={{
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: TradesInnerRenderer,
            },
            checkboxSelection: true,
            sortable: !isSimple,
            resizable: !isSimple,
            initialWidth: 600,
            headerName: isSimple 
              ? (groupingMode === "underlying-date" ? "Underlying" : "Trade Date")
              : "Book",
          }}
          suppressRowClickSelection={true}
          sideBar={isSimple ? undefined : {
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressPivotMode: true,
                  suppressValues: true,
                  suppressRowGroups: true,
                },
              },
            ],
            position: "left",
            defaultToolPanel: "filters",
          }}
          onRowDragMove={onRowDragMove}
          onRowDragEnd={onRowDragEndWithModals}
          getRowId={(params: GetRowIdParams) => {
            return params.data.id;
          }}
          loadingOverlayComponent={loadingOverlayComponent}
          loadingOverlayComponentParams={loadingOverlayComponentParams}
          noRowsOverlayComponent={noRowsOverlayComponent}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          onRowGroupOpened={(params) => {
            gridState.performGroupChanged(params);
          }}
          onSelectionChanged={onSelectionChanged}
          onGridReady={handleGridReady}
          onFirstDataRendered={handleFirstDataRendered}
          onColumnEverythingChanged={gridState.onColumnChanged}
          onColumnResized={gridState.onColumnResized}
          onSortChanged={gridState.onColumnChanged}
          onColumnMoved={gridState.onColumnMoved}
        />
      </GridContainer>
      {/* </Show> */}
    </>
  );
};
